import { useEffect, useState } from "react";
import {
  CalenderDay,
  CalenderGrid,
  CalenderHeader,
  CalenderHeaderDate,
  CalenderHeaderDateWrapper,
  CalenderWrapper,
  DashboardCalenderWrapper,
} from "./style";
import { useRecoilValue } from "recoil";
import { WideState } from "../../../../recoil/wide";

interface CalenderProps {
  firstDate?: string;
  setFirstDate?: (value: string) => void;
  setLastDate?: (value: string) => void;
  dailyDate?: string;
  setDailyDate?: (value: string) => void;
  calenderOpen: boolean;
  setCalenderOpen: (value: boolean) => void;
  getData: () => void;
  type: string;
}

const ReportCalender = ({
  setFirstDate,
  setLastDate,
  calenderOpen,
  setCalenderOpen,
  getData,
  dailyDate,
  setDailyDate,
  type,
}: CalenderProps) => {
  const [days, setDays] = useState<number[]>([]);
  const [daysEmpty, setDaysEmpty] = useState<number[]>([]);
  const [selectedDay1, setSelectedDay1] = useState<string>("");
  const [selectedDay2, setSelectedDay2] = useState<string>("");
  const isWide = useRecoilValue(WideState);

  const getTodayDate = () => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const [currentMonthLabel, setCurrentMonthLabel] = useState<string>(
    getTodayDate()
  );

  const week = ["일", "월", "화", "수", "목", "금", "토"];

  const isToday = (day: number) => {
    const [curYear, curMonth] = currentMonthLabel.split("-");
    const [todayYear, todayMonth, todayDay] = getTodayDate().split("-");
    return (
      curMonth === todayMonth && curYear === todayYear && +todayDay === day
    );
  };

  const changeMonth = (offset: number) => {
    const current = new Date(currentMonthLabel);
    current.setMonth(current.getMonth() + offset);
    const newLabel = current.toISOString().split("T")[0];
    setCurrentMonthLabel(newLabel);
  };

  const formatLabelDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}월`;
  };

  const getDateFromDayNumber = (day: number) => {
    const current = new Date(currentMonthLabel);
    const date = new Date(current.getFullYear(), current.getMonth(), day + 1);
    return date.toISOString().split("T")[0];
  };

  const handleDate = (day: number) => {
    const newDate = getDateFromDayNumber(day);

    switch (type) {
      case "dashboard":
        if (!setDailyDate) return;
        setSelectedDay1(newDate);
        setDailyDate(newDate);
        setCalenderOpen(false);
        break;
      case "report":
        if (!setFirstDate || !setLastDate) return;

        if (!selectedDay1) {
          setSelectedDay1(newDate);
          setFirstDate(newDate);
          setLastDate("");
        } else if (!selectedDay2) {
          setSelectedDay2(newDate);
          const date1 = new Date(selectedDay1);
          const date2 = new Date(newDate);

          if (date2 < date1) {
            setFirstDate(newDate);
            setLastDate(selectedDay1);
          } else {
            setFirstDate(selectedDay1);
            setLastDate(newDate);
          }
        } else {
          setSelectedDay1(newDate);
          setSelectedDay2("");
          setFirstDate(newDate);
          setLastDate("");
        }
        break;
    }
  };

  const isDateInRange = (day: number) => {
    if (!selectedDay1 || !selectedDay2) return false;
    const date = new Date(getDateFromDayNumber(day));
    const start = new Date(
      Math.min(
        new Date(selectedDay1).getTime(),
        new Date(selectedDay2).getTime()
      )
    );
    const end = new Date(
      Math.max(
        new Date(selectedDay1).getTime(),
        new Date(selectedDay2).getTime()
      )
    );

    return date > start && date < end;
  };

  const isDaySelected = (day: number): boolean => {
    const date = getDateFromDayNumber(day);
    return date === selectedDay1 || date === selectedDay2;
  };

  const renderDays = () => {
    const current = new Date(currentMonthLabel);
    const firstDay = new Date(
      current.getFullYear(),
      current.getMonth(),
      1
    ).getDay(); // 해당 월의 1일이 무슨 요일인지
    const lastDate = new Date(
      current.getFullYear(),
      current.getMonth() + 1,
      0
    ).getDate(); // 해당 월의 마지막 날

    setDaysEmpty(Array.from({ length: firstDay }, () => 0));
    setDays(Array.from({ length: lastDate }, (_, i) => i + 1));
  };

  useEffect(() => {
    renderDays();
  }, [currentMonthLabel]);

  useEffect(() => {
    if (dailyDate) {
      setSelectedDay1(dailyDate);
    } else {
      setSelectedDay1("");
      setSelectedDay2("");
    }
  }, [calenderOpen]);

  const resetDate = () => {
    if (!setFirstDate || !setLastDate) return;
    setSelectedDay1("");
    setSelectedDay2("");
    setLastDate("");
    setFirstDate("");
  };

  const saveDate = () => {
    getData();
    setCalenderOpen(false);
  };

  const getDayClassName = (day: number) => {
    if (isDaySelected(day)) return "select";
    if (isDateInRange(day)) return "blur";
    if (isToday(day)) return "today";
    return "";
  };

  return (
    <DashboardCalenderWrapper onClick={(e) => e.stopPropagation()}>
      {type === "dashboard" && isWide && (
        <div className="date" onClick={() => setCalenderOpen(!calenderOpen)}>
          {dailyDate}
        </div>
      )}
      {calenderOpen && (
        <CalenderWrapper isTop={type === "report"} isWide={isWide}>
          <CalenderHeader>
            <div
              className="title"
              style={{
                fontSize: isWide ? "26px" : "20px",
              }}
            >
              날짜를 선택해주세요.
              {!isWide && (
                <img
                  src="image-web/solution/close.svg"
                  alt="close"
                  onClick={() => setCalenderOpen(false)}
                />
              )}
            </div>
            <CalenderHeaderDateWrapper isWide={isWide}>
              <CalenderHeaderDate>
                <img
                  onClick={() => changeMonth(-1)}
                  src="/image-web/solution/calender-left-arrow.svg"
                  alt="previous month"
                />
                <div
                  className="date"
                  style={{
                    width: isWide ? "95px" : "70px",
                  }}
                >
                  {formatLabelDate(currentMonthLabel)}
                </div>
                <img
                  onClick={() => changeMonth(1)}
                  src="/image-web/solution/calender-right-arrow.svg"
                  alt="next month"
                />
              </CalenderHeaderDate>
              {type === "report" && (
                <div className="reset" onClick={resetDate}>
                  초기화
                </div>
              )}
            </CalenderHeaderDateWrapper>
          </CalenderHeader>
          <CalenderGrid isWide={isWide}>
            {week.map((label) => (
              <div key={label} className="label">
                {label}
              </div>
            ))}
            {daysEmpty.map((_, index) => (
              <div key={index} className="empty"></div>
            ))}
            {days.map((day) => (
              <CalenderDay
                key={day}
                type={getDayClassName(day)}
                isWide={isWide}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDate(day);
                }}
              >
                {day}
              </CalenderDay>
            ))}
          </CalenderGrid>
          {type === "report" && (
            <div className="button" onClick={saveDate}>
              선택
            </div>
          )}
        </CalenderWrapper>
      )}
    </DashboardCalenderWrapper>
  );
};

export default ReportCalender;
